/**
 * Client-side access to the acquisition scanner's leads (W13): listings it
 * found before any IM exists, waiting for Andi to approve or reject them.
 * NUMERIC columns arrive as strings, timestamps as ISO strings.
 */

import { AuthExpiredError } from './ims.js';

export type LeadStatus = 'new' | 'approved' | 'rejected' | 'requested' | 'request_failed';
export type RequestMechanism =
  | 'knightsbridge_nda'
  | 'web_form'
  | 'email'
  | 'account_required'
  | 'manual';

export interface Lead {
  id: string;
  source: string;
  source_ref: string;
  title: string;
  url: string | null;
  country: string | null;
  currency: string | null;
  revenue: string | null;
  ebitda: string | null;
  asking_price: string | null;
  summary: string | null;
  flags: string[];
  verdict: 'match' | 'near_miss';
  request_mechanism: RequestMechanism;
  contact_email: string | null;
  deadline: string | null;
  possible_duplicate: string | null;
  status: LeadStatus;
  request_result: string | null;
  found_at: string;
  last_seen_at: string;
  decided_at: string | null;
  requested_at: string | null;
  chase_count: number;
  last_chased_at: string | null;
  replied_at: string | null;
  /** Why the robot stopped chasing and handed the lead to Andi. */
  chase_flag: string | null;
}

export interface LeadScan {
  id: string;
  mode: 'scan' | 'requests';
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'ok' | 'failed';
  leads_found: number;
  requests_made: number;
  detail: unknown;
  error: string | null;
}

/** Mechanisms the request job can carry out itself. */
export const AUTOMATABLE: readonly RequestMechanism[] = ['knightsbridge_nda', 'web_form', 'email'];

export const MECHANISM_LABELS: Record<RequestMechanism, string> = {
  knightsbridge_nda: 'Knightsbridge NDA',
  web_form: 'Enquiry form',
  email: 'Email',
  account_required: 'Needs an account',
  manual: 'Manual',
};

async function check(res: Response, what: string): Promise<Response> {
  if (res.status === 401 || res.status === 403) throw new AuthExpiredError();
  if (!res.ok) throw new Error(`Couldn't ${what} (${res.status})`);
  return res;
}

export async function listLeads(): Promise<Lead[]> {
  return (await (await check(await fetch('/api/leads'), 'load leads')).json()) as Lead[];
}

export async function listLeadScans(limit = 5): Promise<LeadScan[]> {
  const res = await fetch(`/api/leads/scans?limit=${encodeURIComponent(String(limit))}`);
  return (await (await check(res, "load the scanner's runs")).json()) as LeadScan[];
}

export async function decideLead(id: string, status: 'approved' | 'rejected'): Promise<Lead> {
  const res = await fetch(`/api/leads/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  return (await (await check(res, 'save that decision')).json()) as Lead;
}

export async function startScan(): Promise<void> {
  await check(await fetch('/api/leads/scan', { method: 'POST' }), 'start a scan');
}
